import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import LinkIcon from "@mui/icons-material/Link";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import SendIcon from "@mui/icons-material/Send";
import { createInvite, pendingInviteFromError, resendInvite } from "~/services/admin/access";
import { listDomains } from "~/services/admin/domains";
import { copyText } from "~/utils/clipboard";
import { getApiErrorCode } from "~/utils/apiError";
import AppDialog from "~/shared/ui/AppDialog";
import { COLORS } from "~/ui/tokens";
import { accessErrorMessage, emailDomain, formatDateBR, inviteEmailErrorLabel, ROLE_OPTIONS } from "./labels";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const roleValue = (role) => role ?? "";
// 409 INVITE_ALREADY_PENDING (P1-4). Textos sem mockup: Apêndice.
const pendingMessage = ({ status, expiresAt }) =>
  status === "expired"
    ? `Este e-mail já tem um convite, vencido em ${formatDateBR(expiresAt)}. Reenvie para gerar um novo link.`
    : `Este e-mail já tem um convite pendente, válido até ${formatDateBR(expiresAt)}. Reenvie para gerar um novo link.`;

export default function InviteDialog({ open, onClose, onInvited, companies = [], initialEmail = "", intent = "invite" }) {
  const [email, setEmail] = useState(initialEmail);
  const [name, setName] = useState("");
  const [role, setRole] = useState(null);
  const [companyIds, setCompanyIds] = useState([]);
  const [domains, setDomains] = useState(null);
  const [serverWantsConfirm, setServerWantsConfirm] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [sending, setSending] = useState(null);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(null); // { inviteId, status, expiresAt, mode }
  const [created, setCreated] = useState(null);
  const [manualLink, setManualLink] = useState(null);

  useEffect(() => {
    if (!open) return;
    setEmail(initialEmail);
    setName("");
    setRole(null);
    setCompanyIds([]);
    setServerWantsConfirm(false);
    setConfirmed(false);
    setError(null);
    setPending(null);
    setCreated(null);
    setManualLink(null);
    let alive = true;
    listDomains().then((list) => alive && setDomains(list.map((d) => d.domain)), () => alive && setDomains(null));
    return () => {
      alive = false;
    };
  }, [open, initialEmail]);

  const normalized = email.trim().toLowerCase();
  const domain = emailDomain(normalized);
  const emailValid = EMAIL.test(normalized);
  const external = emailValid && (serverWantsConfirm || (domains !== null && !domains.includes(domain)));
  const noAccess = role === null && companyIds.length === 0;
  // Modo reenvio (Tentativas negadas): o BE checa convite pendente ANTES de NO_ACCESS_GRANTED (L20),
  // então basta o e-mail — o 409 INVITE_ALREADY_PENDING traz o inviteId para o reenvio.
  const isResend = intent === "resend";
  const canSend = emailValid && (isResend || !noAccess) && (!external || confirmed) && !sending && !created;

  // Trocar o e-mail invalida o 409/422 do endereço anterior: senão "Reenviar convite" reenviaria o convite antigo.
  const changeEmail = (e) => {
    setEmail(e.target.value);
    setPending(null);
    setError(null);
    setServerWantsConfirm(false);
  };

  const toggleCompany = (id) => setCompanyIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  const finish = (result, message) => {
    onInvited?.(result, message);
    onClose?.();
  };

  // "send" e "copy" fazem o MESMO POST (o e-mail sempre sai); "copy" só copia o inviteUrl depois (P1-11).
  const submit = async (mode) => {
    setSending(mode);
    setError(null);
    setPending(null);
    const body = { email: normalized, role, companyIds };
    if (name.trim()) body.name = name.trim();
    if (external) body.confirmExternal = confirmed;
    try {
      const result = await createInvite(body);
      if (result.invite?.emailStatus === "failed") {
        setCreated(result);
        onInvited?.(result, null);
        return;
      }
      if (mode === "copy") {
        if (await copyText(result.inviteUrl)) {
          finish(result, `Convite enviado e link copiado. Ele expira em ${formatDateBR(result.invite?.expiresAt)}.`);
        } else {
          setCreated(result);
          setManualLink(result.inviteUrl);
          onInvited?.(result, null);
        }
        return;
      }
      finish(result, `Convite enviado para ${normalized}.`);
    } catch (e) {
      const code = getApiErrorCode(e);
      if (code === "EXTERNAL_CONFIRMATION_REQUIRED") {
        setServerWantsConfirm(true);
        setConfirmed(false);
        setError("Confirme que é um e-mail externo para continuar.");
      } else if (pendingInviteFromError(e)) {
        const target = { ...pendingInviteFromError(e), mode };
        setPending(target);
        setError(pendingMessage(target));
        // Aberto por "Reenviar convite" (Tentativas negadas): o aviso promete reenviar com UM clique,
        // então o 409 INVITE_ALREADY_PENDING já dispara o reenvio (final review M1).
        if (isResend) await resendPending(target);
      } else {
        setError(accessErrorMessage(e, "Não foi possível criar o convite."));
      }
    } finally {
      setSending(null);
    }
  };

  // Reenvio com o inviteId do 409 (pendente vencido ou não). O anterior vira "superseded" no BE (P1-8).
  // target = convite do 409; o botão "Reenviar convite" usa o que está em `pending`.
  const resendPending = async (target = pending) => {
    setSending("resend");
    try {
      const result = await resendInvite(target.inviteId);
      if (result.invite?.emailStatus === "failed") {
        setPending(null);
        setError(null);
        setCreated(result);
        onInvited?.(result, null);
        return;
      }
      if (target.mode === "copy") {
        if (await copyText(result.inviteUrl)) {
          finish(result, `Convite reenviado e link copiado. Ele expira em ${formatDateBR(result.invite?.expiresAt)}.`);
        } else {
          setPending(null);
          setError(null);
          setCreated(result);
          setManualLink(result.inviteUrl);
          onInvited?.(result, null);
        }
        return;
      }
      finish(result, "Convite reenviado. O link anterior deixou de valer.");
    } catch (e) {
      setError(accessErrorMessage(e, "Não foi possível reenviar o convite."));
    } finally {
      setSending(null);
    }
  };

  const copyCreated = async () => {
    if (await copyText(created.inviteUrl)) finish(created, `Link do convite copiado. Ele expira em ${formatDateBR(created.invite?.expiresAt)}.`);
    else setManualLink(created.inviteUrl);
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      busy={Boolean(sending)}
      size="sm"
      title="Convidar pessoa"
      actions={
        created ? (
          <>
            <Button startIcon={<LinkIcon />} variant="outlined" onClick={copyCreated}>Copiar link</Button>
            <Button variant="contained" onClick={onClose}>Fechar</Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={Boolean(sending)} sx={{ color: "text.primary" }}>Cancelar</Button>
            <LoadingButton variant="outlined" startIcon={<LinkIcon />} loading={sending === "copy"} disabled={!canSend} onClick={() => submit("copy")}>
              Criar e copiar link
            </LoadingButton>
            <LoadingButton variant="contained" startIcon={<SendIcon />} loading={sending === "send"} disabled={!canSend} onClick={() => submit("send")}>
              Enviar convite
            </LoadingButton>
          </>
        )
      }
    >
      {intent === "resend" && !created && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Este e-mail tem um convite pendente. Clique em Enviar convite para reenviá-lo com um link novo.
        </Alert>
      )}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5, pt: 1 }}>
        <TextField label="E-mail" required value={email} onChange={changeEmail} disabled={Boolean(created)} autoFocus type="email" />
        <TextField label="Nome" value={name} onChange={(e) => setName(e.target.value)} disabled={Boolean(created)} />
      </Box>
      {external && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <b>E-mail externo</b> — {domain} não está nos domínios do grupo.
          <FormControlLabel
            sx={{ display: "flex", mt: 0.5 }}
            control={<Checkbox checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />}
            label="Confirmo que é um e-mail externo"
          />
        </Alert>
      )}
      <TextField
        select
        fullWidth
        label="Papel no Antecipe"
        value={roleValue(role)}
        onChange={(e) => setRole(e.target.value === "" ? null : e.target.value)}
        SelectProps={{ native: true }}
        InputLabelProps={{ shrink: true }} // B-06: com valor "" (Nenhum) o MUI não encolhe o rótulo e ele cobre a opção exibida
        disabled={Boolean(created)}
        sx={{ mt: 2 }}
      >
        {ROLE_OPTIONS.map((o) => <option key={o.label} value={roleValue(o.value)}>{o.label}</option>)}
      </TextField>
      <Typography sx={{ mt: 2, mb: 0.5, fontSize: 12, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", color: COLORS.muted }}>Gestor das empresas</Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" } }}>
        {/* Empresa inativa não recebe convite novo (422 COMPANY_INACTIVE). */}
        {companies.filter((c) => c.active !== false).map((c) => (
          <FormControlLabel key={c.id} control={<Checkbox checked={companyIds.includes(c.id)} onChange={() => toggleCompany(c.id)} disabled={Boolean(created)} />} label={c.name} />
        ))}
      </Box>
      {emailValid && noAccess && !isResend && <Typography variant="body2" color="error" sx={{ mt: 1 }}>Escolha um papel ou pelo menos uma empresa.</Typography>}
      <Typography variant="caption" component="p" sx={{ color: COLORS.muted, mt: 1.5, display: "flex", gap: 0.5 }}>
        <MailOutlineIcon sx={{ fontSize: 16 }} aria-hidden />
        <span>E-mail de nao-responda@antecipepagamentos.com.br · válido por 7 dias · depois você pode copiar o link.</span>
      </Typography>
      {error && (
        <Alert
          sx={{ mt: 2 }}
          severity={pending ? "warning" : "error"}
          action={pending ? <LoadingButton color="inherit" size="small" loading={sending === "resend"} onClick={() => resendPending()}>Reenviar convite</LoadingButton> : undefined}
        >
          {error}
        </Alert>
      )}
      {created?.invite?.emailStatus === "failed" && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <span>E-mail falhou — use Copiar link</span>
          {inviteEmailErrorLabel(created.invite.emailError) && <Box component="span" sx={{ display: "block" }}>{inviteEmailErrorLabel(created.invite.emailError)}</Box>}
        </Alert>
      )}
      {manualLink && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2">Copie o link abaixo:</Typography>
          <TextField size="small" fullWidth value={manualLink} InputProps={{ readOnly: true }} onFocus={(e) => e.target.select()} />
        </Box>
      )}
    </AppDialog>
  );
}
